import React from 'react';
import type { GiftProgressState } from '../../offer.types';
import { formatCurrency } from '../../utils/formatting';
import GiftProgressBar from './GiftProgressBar';

interface GiftCardProps {
  state: GiftProgressState;
  onApplyCoupon?: (couponCode: string) => Promise<void> | void;
  /** Compact variant rendered inside the OrderSummaryCard */
  compact?: boolean;
  loading?: boolean;
}

const GiftCard: React.FC<GiftCardProps> = ({ state, onApplyCoupon, compact = false, loading = false }) => {
  const { gift, status, progress, remainingValue, remainingQuantity } = state;
  const needsCoupon = status === 'unlocked_needs_coupon';

  const message = (() => {
    if (status === 'unlocked') return 'Você ganhou o brinde!';
    if (status === 'unlocked_needs_coupon') {
      return `Aplique o cupom ${gift.couponCodeRequired ?? ''} para ganhar`;
    }
    if (gift.triggerType === 'value' && remainingValue != null && remainingValue > 0) {
      return `Faltam ${formatCurrency(remainingValue)} para ganhar`;
    }
    if (gift.triggerType === 'quantity' && remainingQuantity != null && remainingQuantity > 0) {
      return `Faltam ${remainingQuantity} item${remainingQuantity > 1 ? 'ns' : ''} para ganhar`;
    }
    return 'Em breve você ganhará um brinde';
  })();

  const handleApply = async () => {
    if (loading || !onApplyCoupon || !gift.couponCodeRequired) return;
    await onApplyCoupon(gift.couponCodeRequired);
  };

  const containerClasses = compact
    ? 'rounded-md bg-white p-2.5 dark:bg-gray-900'
    : 'rounded-lg bg-white p-3 dark:bg-gray-900';

  const imageSize = compact ? 'h-12 w-12' : 'h-14 w-14';
  const titleClass = compact ? 'text-xs font-medium' : 'text-sm font-medium';

  return (
    <div className={containerClasses}>
      <div className="flex items-start gap-3">
        {gift.product.imageUrl ? (
          <img
            src={gift.product.imageUrl}
            alt={gift.product.title}
            className={`${imageSize} flex-shrink-0 rounded-md object-cover`}
          />
        ) : (
          <div
            className={`${imageSize} flex flex-shrink-0 items-center justify-center rounded-md bg-gray-100 text-gray-400 dark:bg-gray-800`}
            aria-hidden="true"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 12 20 22 4 22 4 12" />
              <rect x="2" y="7" width="20" height="5" />
              <line x1="12" y1="22" x2="12" y2="7" />
              <path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z" />
              <path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z" />
            </svg>
          </div>
        )}

        <div className="min-w-0 flex-1">
          <h3 className={`truncate ${titleClass} text-gray-900 dark:text-gray-100`}>
            {gift.product.title}
          </h3>
          <div className={`${compact ? 'text-[11px]' : 'text-xs'} my-1 text-gray-600 dark:text-gray-300`}>
            {message}
          </div>

          {status === 'unlocked' ? (
            <div className="mt-1 flex items-baseline gap-2">
              {gift.product.price != null && gift.product.price > 0 ? (
                <span className="text-xs text-gray-500 line-through dark:text-gray-400">
                  {formatCurrency(gift.product.price)}
                </span>
              ) : null}
              <span className="text-sm font-bold text-emerald-600 dark:text-emerald-400">
                Grátis
              </span>
            </div>
          ) : status === 'locked' ? (
            <div className="mt-2">
              <GiftProgressBar progress={progress} />
            </div>
          ) : null}

          {needsCoupon && onApplyCoupon ? (
            <button
              type="button"
              onClick={handleApply}
              disabled={loading}
              className="inline-flex w-full items-center justify-center rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-emerald-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
            >
              Aplicar cupom {gift.couponCodeRequired}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default GiftCard;
